import type { HTMLAttributes, ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../cn.js';
import type { BadgeTone } from './Badge.js';

export type BannerTone = BadgeTone;

/** Same tone vocabulary as `Badge`/`StatusPill` — see `Badge.ts`'s `statusTone` for why colour is
 *  never the only signal (the icon/label a caller puts in `children` carries the meaning; the tone
 *  is reinforcement, not the whole message). */
const TONE_STYLES: Record<BannerTone, string> = {
  neutral: 'border-border bg-bg-subtle text-fg-muted',
  success: 'border-success/30 bg-success-subtle text-success',
  warning: 'border-warning/30 bg-warning-subtle text-warning',
  danger: 'border-danger/30 bg-danger-subtle text-danger',
  info: 'border-info/30 bg-info-subtle text-info',
  accent: 'border-accent/30 bg-accent-subtle text-accent',
};

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: BannerTone;
  /** A small leading glyph — an icon component instance, e.g. `<TriangleAlert className="size-4" />`. */
  icon?: ReactNode;
  children: ReactNode;
  /** Trailing action slot — a button, e.g. "Submit regularisation" on a training-overrun banner. */
  action?: ReactNode;
  /** Renders a dismiss (×) control that calls this when clicked. Omit entirely for a PERSISTENT
   *  banner with no dismiss control at all — e.g. a training-overrun gate the viewer cannot make go
   *  away by clicking; only clearing the underlying condition (finishing the task) does that. */
  onDismiss?: () => void;
  /** Pins the banner to the top of its scroll container instead of flowing inline — for a banner
   *  that must stay visible with a live countdown while the page scrolls underneath it. Below
   *  `Modal`'s `z-50` and above `Table`'s sticky header/pinned column (`z-20`/`z-30`), so an open
   *  modal still wins and a sticky banner still clears a scrolled table. */
  sticky?: boolean;
}

/**
 * A full-width status strip — an informational note, a warning, or a persistent gate (the training
 * module's overrun banner, which pairs `sticky` with a live `CountdownTimer`/`ElapsedTimer` in
 * `children` and an action button that opens the regularisation form). Distinct from a toast: it
 * does not auto-dismiss and does not stack — one banner, rendered where the page decides it belongs.
 */
export function Banner({
  tone = 'info',
  icon,
  children,
  action,
  onDismiss,
  sticky = false,
  className,
  ...rest
}: BannerProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm',
        sticky && 'sticky top-0 z-40',
        TONE_STYLES[tone],
        className,
      )}
      {...rest}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {icon && (
          <span className="shrink-0" aria-hidden="true">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {action}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="rounded p-1 opacity-70 hover:opacity-100"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
}
